/**
 * Amazon Orders Store — Jotai atoms with polling for real-time order dashboard.
 */

import { atom } from 'jotai'
import { useEffect, useRef } from 'react'
import { useSetAtom, useAtomValue } from 'jotai'

const API_BASE = import.meta.env.VITE_API_BASE || ''

// ─── Types ──────────────────────────────────────────────────────────────────

export interface OrderMoney {
  CurrencyCode: string
  Amount: string
}

export interface AmazonOrderItem {
  ASIN: string
  SellerSKU?: string
  OrderItemId: string
  Title?: string
  QuantityOrdered: number
  QuantityShipped: number
  ItemPrice?: OrderMoney
  ItemTax?: OrderMoney
  ShippingPrice?: OrderMoney
  ShippingTax?: OrderMoney
  PromotionDiscount?: OrderMoney
}

export interface AmazonOrder {
  AmazonOrderId: string
  PurchaseDate: string
  LastUpdateDate: string
  OrderStatus: string
  OrderTotal?: OrderMoney
  NumberOfItemsShipped: number
  NumberOfItemsUnshipped: number
  MarketplaceId: string
  FulfillmentChannel: string
  ShipServiceLevel?: string
  ShippingAddress?: {
    StateOrRegion?: string
    City?: string
    CountryCode?: string
  }
  OrderItems?: AmazonOrderItem[]
}

export interface TodayStats {
  ordersToday: number
  revenueToday: number
  currencyCode: string
  pendingCount: number
  shippedCount: number
}

// ─── sessionStorage cache ───────────────────────────────────────────────────

const CACHE_KEY = 'vertco_amazon_orders'
const SEEN_KEY = 'vertco_amazon_orders_seen'

function readOrdersCache(): AmazonOrder[] | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as AmazonOrder[]
  } catch {
    return null
  }
}

function writeOrdersCache(orders: AmazonOrder[]) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(orders))
  } catch { /* quota exceeded */ }
}

function readSeenIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY)
    if (!raw) return new Set()
    return new Set(JSON.parse(raw) as string[])
  } catch {
    return new Set()
  }
}

function writeSeenIds(ids: Set<string>) {
  try {
    sessionStorage.setItem(SEEN_KEY, JSON.stringify([...ids]))
  } catch { /* ignore */ }
}

// ─── Core atoms ─────────────────────────────────────────────────────────────

const _rawOrdersAtom = atom<AmazonOrder[] | null>(null)

/** All orders — persisted via sessionStorage */
export const ordersAtom = atom(
  (get): AmazonOrder[] => {
    const val = get(_rawOrdersAtom)
    if (val !== null) return val
    return readOrdersCache() ?? []
  },
  (_get, set, orders: AmazonOrder[]) => {
    set(_rawOrdersAtom, orders)
    writeOrdersCache(orders)
  },
)

const _rawSeenIdsAtom = atom<Set<string> | null>(null)

/** Set of order IDs already displayed (for detecting new arrivals) */
export const seenOrderIdsAtom = atom(
  (get): Set<string> => {
    const val = get(_rawSeenIdsAtom)
    if (val !== null) return val
    return readSeenIds()
  },
  (_get, set, ids: Set<string>) => {
    set(_rawSeenIdsAtom, ids)
    writeSeenIds(ids)
  },
)

export const loadingAtom = atom(false)
export const errorAtom = atom<string | null>(null)
export const fetchedAtom = atom(false)

// ─── Derived atoms ──────────────────────────────────────────────────────────

/** Orders that haven't been seen yet — triggers animation */
export const newOrdersAtom = atom((get): AmazonOrder[] => {
  const orders = get(ordersAtom)
  const seen = get(seenOrderIdsAtom)
  if (seen.size === 0) return [] // Don't animate on first load
  return orders.filter((o) => !seen.has(o.AmazonOrderId))
})

/** Today's aggregated stats */
export const todayStatsAtom = atom((get): TodayStats => {
  const orders = get(ordersAtom)
  let revenue = 0
  let currencyCode = 'USD'
  let pendingCount = 0
  let shippedCount = 0

  for (const o of orders) {
    const status = o.OrderStatus.toLowerCase()
    if (status === 'pending' || status === 'unshipped' || status === 'partiallyshipped') {
      pendingCount++
    } else if (status === 'shipped') {
      shippedCount++
    }

    // Only count sales from non-cancelled, non-pending orders
    if (status !== 'canceled' && status !== 'cancelled' && status !== 'pending') {
      // Prefer ItemPrice sum (product sales only) over OrderTotal (includes shipping/tax)
      if (o.OrderItems && o.OrderItems.length > 0) {
        for (const item of o.OrderItems) {
          if (item.ItemPrice?.Amount) {
            revenue += parseFloat(item.ItemPrice.Amount)
            currencyCode = item.ItemPrice.CurrencyCode || 'USD'
          }
        }
      } else if (o.OrderTotal?.Amount) {
        // Fallback to OrderTotal if items not yet loaded
        revenue += parseFloat(o.OrderTotal.Amount)
        currencyCode = o.OrderTotal.CurrencyCode || 'USD'
      }
    }
  }

  return {
    ordersToday: orders.length,
    revenueToday: revenue,
    currencyCode,
    pendingCount,
    shippedCount,
  }
})

// ─── Action atoms ───────────────────────────────────────────────────────────

/** Whether order items have been loaded (only need to fetch once per session) */
export const itemsFetchedAtom = atom(false)

/** Fetch orders from backend */
export const fetchOrdersAtom = atom(null, async (get, set) => {
  set(loadingAtom, true)
  set(errorAtom, null)

  try {
    // Start of today UTC
    const now = new Date()
    const startOfToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))

    // Include order items on first fetch (for accurate product sales data)
    // Skip on subsequent polls to keep them fast
    const needItems = !get(itemsFetchedAtom)
    const itemsParam = needItems ? '&includeItems=true' : ''

    const res = await fetch(
      `${API_BASE}/amazon-orders/recent?createdAfter=${startOfToday.toISOString()}${itemsParam}`,
    )
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data as any).error || `Request failed (${res.status})`)
    }

    const data = (await res.json()) as { orders: AmazonOrder[]; count: number }

    // On first fetch, mark all existing orders as "seen" (don't animate them)
    const wasFirstFetch = !get(fetchedAtom)
    if (wasFirstFetch) {
      const allIds = new Set(data.orders.map((o) => o.AmazonOrderId))
      set(seenOrderIdsAtom, allIds)
    }

    // If we got items data, merge it into existing orders for subsequent polls
    if (needItems) {
      set(ordersAtom, data.orders)
      // Check if any order actually has items (enrichment succeeded)
      const hasItems = data.orders.some((o) => o.OrderItems && o.OrderItems.length > 0)
      if (hasItems) set(itemsFetchedAtom, true)
    } else {
      // On polling updates: preserve OrderItems from previous fetch,
      // merge with new order-level data (status changes, new orders)
      const prevOrders = get(ordersAtom)
      const prevItemsMap = new Map<string, AmazonOrderItem[]>()
      for (const o of prevOrders) {
        if (o.OrderItems && o.OrderItems.length > 0) {
          prevItemsMap.set(o.AmazonOrderId, o.OrderItems)
        }
      }

      const mergedOrders = data.orders.map((o) => ({
        ...o,
        OrderItems: o.OrderItems || prevItemsMap.get(o.AmazonOrderId),
      }))
      set(ordersAtom, mergedOrders)
    }

    set(fetchedAtom, true)
  } catch (err) {
    set(errorAtom, err instanceof Error ? err.message : 'Failed to fetch orders')
  } finally {
    set(loadingAtom, false)
  }
})

/** Mark all current orders as seen (call after animation completes) */
export const markAllSeenAtom = atom(null, (get, set) => {
  const orders = get(ordersAtom)
  const seen = get(seenOrderIdsAtom)
  const updated = new Set(seen)
  for (const o of orders) {
    updated.add(o.AmazonOrderId)
  }
  set(seenOrderIdsAtom, updated)
})

// ─── Polling hook ───────────────────────────────────────────────────────────

/**
 * Custom hook that polls for new orders at a regular interval.
 * Fetches immediately on mount, then every `intervalMs` (default 30s).
 * Cleans up on unmount.
 */
export function useOrdersPolling(intervalMs = 30_000) {
  const fetchOrders = useSetAtom(fetchOrdersAtom)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    // Fetch immediately
    fetchOrders()

    // Set up polling
    intervalRef.current = setInterval(() => {
      fetchOrders()
    }, intervalMs)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [fetchOrders, intervalMs])
}
